import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';

import { API_BASE_URL } from '../../api';
import { isAnonymousApiPath, isPublicQuoteApiPath } from '../api/anonymous-endpoints';
import { AuthFacade } from '../services/auth.facade';
import { TokenService } from '../services/token.service';

let isRefreshing = false;
const refreshedToken$ = new BehaviorSubject<string | null>(null);

/**
 * Single transport policy for every backend request (ADR-015, api-client-nswag.md §7):
 *
 * - sends cookies (`withCredentials`) on requests targeting `API_BASE_URL`, so the
 *   HttpOnly refresh cookie flows without per-service opt-ins; public quote links are
 *   credentialless and are the explicit exception;
 * - attaches the in-memory access token, except on the anonymous whitelist;
 * - on a 401 from a protected endpoint, refreshes once via `AuthFacade` (queuing
 *   concurrent 401s behind the same refresh call) and retries. If the refresh itself
 *   fails, clears the session and sends the user to /login.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const authFacade = inject(AuthFacade);
  const router = inject(Router);
  const apiBaseUrl = inject(API_BASE_URL, { optional: true });

  const isBackendRequest = apiBaseUrl !== null && req.url.startsWith(apiBaseUrl);
  const isAnonymousPath = isAnonymousApiPath(req.url);
  const isPublicQuotePath = isPublicQuoteApiPath(req.url);
  const token = tokenService.accessToken();

  let outgoingReq = req;
  if (isBackendRequest && !isPublicQuotePath) {
    outgoingReq = outgoingReq.clone({ withCredentials: true });
  }
  if (token && !isAnonymousPath) {
    outgoingReq = outgoingReq.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  return next(outgoingReq).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401 && !isAnonymousPath) {
        return refreshAndRetry(outgoingReq, next, authFacade, tokenService, router);
      }

      return throwError(() => error);
    }),
  );
};

function refreshAndRetry(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authFacade: AuthFacade,
  tokenService: TokenService,
  router: Router,
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshedToken$.next(null);

    return authFacade.refreshSession().pipe(
      switchMap((accessToken) => {
        isRefreshing = false;
        refreshedToken$.next(accessToken);
        return next(req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } }));
      }),
      catchError((refreshError: unknown) => {
        isRefreshing = false;
        tokenService.clearSession();
        void router.navigate(['/login']);
        return throwError(() => refreshError);
      }),
    );
  }

  // A refresh triggered by a concurrent request is already in flight — wait for it.
  return refreshedToken$.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((token) => next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))),
  );
}
